/**
 * 会话上下文（P-01 登录 / 会话守卫）。
 *
 * 三个职责：
 * 1. 启动时调 `GET /admin/auth/me` 判断「有没有活着的会话」——401 是正常分支，不是错误。
 * 2. 安装 `client.setUnauthorizedHandler`：运行时收到 9001/401 → 清态 + 跳登录（带回跳地址）。
 * 3. 暴露 login / logout / currentUser，让登录页与顶栏共用同一份会话状态。
 *
 * 会话是服务端可撤销的不透明 Cookie（openapi securitySchemes.adminSession，不是 JWT），
 * 因此前端**不持有也不应保存**任何 token；`via === 'session'` 的判定全部发生在服务端。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, setUnauthorizedHandler } from '../api/client';
import { getCurrentAdmin, login as apiLogin, logout as apiLogout } from '../api/endpoints';
import type { AdminUser, LoginRequest } from '../api/types';

interface AuthContextValue {
  /** 当前登录管理员；未登录为 null。加载中为 undefined（区别于「确认未登录」） */
  currentUser: AdminUser | null | undefined;
  /** 启动探测完成（/me 已返回，无论 200 还是 401） */
  ready: boolean;
  login: (body: LoginRequest) => Promise<AdminUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AdminUser | null | undefined>(undefined);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const admin = await getCurrentAdmin();
      setCurrentUser(admin);
    } catch (error) {
      // 401 是「未登录」，不是故障
      if (error instanceof ApiError && error.isUnauthorized) {
        setCurrentUser(null);
        return;
      }
      // 传输层失败（后端没起）：保持当前状态，不把「连不上」误判成「未登录」
      setCurrentUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  const login = useCallback(async (body: LoginRequest): Promise<AdminUser> => {
    const admin = await apiLogin(body);
    setCurrentUser(admin);
    setReady(true);
    return admin;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await apiLogout();
    setCurrentUser(null);
    setReady(true);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // 运行时 9001/401 → 清态 + 跳登录。跳转动作放在这里而不是 client.ts：
  // client 层不 import react-router，保持 API 层与 UI 解耦
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setCurrentUser(null);
      setReady(true);
      const current = globalThis.location.pathname + globalThis.location.search;
      if (current !== '/login') {
        globalThis.location.assign(`/login?next=${encodeURIComponent(current)}`);
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ currentUser, ready, login, logout, refresh }),
    [currentUser, ready, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error('useAuth 必须在 <AuthProvider> 内使用');
  return ctx;
}
